import App from '@app/app';
import { OrderLineDetail } from '@app/service/OrderLineFactory';
import Pilot from '@app/constant/Pilot';
import { DIGITAL_IMAGE_CATEGORY } from '@app/core/constants';
import { uniqueBy } from '@app/utils/collection.utils';

export class CustomizationImageService {
  constructor(LooksStore, ShoppingCartStore, ContentStore, ItemService, JobStore, LookAllowlistService) {
    this.looksStore = LooksStore;
    this.shoppingCartStore = ShoppingCartStore;
    this.contentStore = ContentStore;
    this.itemService = ItemService;
    this.jobStore = JobStore;
    this.lookAllowlistService = LookAllowlistService;

    this.context = null;
    this.orderLines = null;
    this._indexMapMasked = null;
    this._indexMap = null;
    this.initialLook = null;
    this._selectedOrderLineDetailIndex = null;
  }

  init(orderLines, context = {}) {
    this.context = {
      ...context,
      subject: this.shoppingCartStore.getCurrentSubject(),
      cartBgIds: this._getCartBgIds(context.editableOrderLine),
      lookWhitelist: this.lookAllowlistService.getWhitelist(context.productSkuCode),
      lookBlacklist: this.lookAllowlistService.getBlacklist(),
      defaultLookBg: this.looksStore.getDefaultLook()?.background
    };

    this.initialLook = null;
    this._selectedOrderLineDetailIndex = null;

    this._setOrderLines(orderLines);
    if (this._indexMap.length) {
      this.selectOrderLindeDetail(0);
    }
    this._initBgSelections();
    this._setProductImages();
    this._selectDefaultLooks();
  }

  applySelectedLookToAll() {
    const selectedLook = this.getSelectedLook();

    this._forEachOrderLineDetail((orderLineDetail, index) => {
      if (this._isOrderLineDetailCustomizable(orderLineDetail)) {
        if (orderLineDetail.look.layoutThemeID !== selectedLook.layoutThemeID) {
          this._updateLook(index, selectedLook);
        }
      }
    });
  }

  selectBg(bgId) {
    const selectedLookDefinition = this.getSelectedLookDefinition();

    const look = this._findLookByCropClickAndBgId(
      selectedLookDefinition.crop,
      selectedLookDefinition.preferredClick,
      bgId
    );

    this._updateSelectedLook(look);
  }

  getSelectedOrderLineDetailIndex(masked = false) {
    return masked
      ? this.getMaskedOrderLindeDetailIndex(this._selectedOrderLineDetailIndex)
      : this._selectedOrderLineDetailIndex;
  }

  getMaskedOrderLindeDetailIndex(orderLineDetailIndex) {
    const index = this._getIndexMap()[orderLineDetailIndex];

    return this._indexMapMasked.findIndex((i) => i === index);
  }

  getSelectedBgId() {
    return this._getSelectedBackground().backgroundImageId;
  }

  selectPoseByLook(look) {
    const lookDefinition = this.looksStore.getLookDefinitionByLook(look);

    this.selectPose(lookDefinition.crop, lookDefinition.preferredClick);
  }

  selectPose(crop, click) {
    const look = this._findLookByCropClickAndBgId(crop, click, this.getSelectedBgId());

    this._updateSelectedLook(look);
  }

  getSelectedLookDefinition() {
    return this.looksStore.getLookDefinitionByLook(this.getSelectedLook());
  }

  selectOrderLindeDetail(index) {
    this._selectedOrderLineDetailIndex = index;

    this.initialLook = this.getSelectedLook();
  }

  selectNextOrderLindeDetail() {
    const nextIndex = this._selectedOrderLineDetailIndex + 1;
    if (nextIndex < this._getIndexMap().length) {
      this.selectOrderLindeDetail(nextIndex);
    }
  }

  selectPrevOrderLindeDetail() {
    const prevIndex = this._selectedOrderLineDetailIndex - 1;
    if (prevIndex >= 0) {
      this.selectOrderLindeDetail(prevIndex);
    }
  }

  getSelectedLook() {
    return this.getSelectedOrderLineDetail().look;
  }

  getBg(index, masked = false) {
    return this.getLook(index, masked)?.background;
  }

  getLook(index, masked = false) {
    return this.getOrderLineDetail(index, masked).look;
  }

  resetSelectedLook() {
    this._updateSelectedLook(this.initialLook);
  }

  getSelectedOrderLineDetail() {
    return this.getOrderLineDetail(this._selectedOrderLineDetailIndex);
  }

  isSelectedOrderLineDetailCustomizable() {
    return this._isOrderLineDetailCustomizable(this.getSelectedOrderLineDetail());
  }

  getOrderLineDetail(index, masked = false) {
    const [orderLineIndex, orderLineDetailIndex] = this._getIndexMap(masked)[index];

    return this.orderLines[orderLineIndex].orderLineDetails[orderLineDetailIndex];
  }

  getOrderLine(index) {
    const [orderLineIndex] = this._getIndexMap()[index];

    return this.orderLines[orderLineIndex];
  }

  isFirstOrderLineDetailSelected() {
    return this._selectedOrderLineDetailIndex === 0;
  }

  getLooksBySelectedBgId() {
    return this._getLookDefinitionsByBgId(this.getSelectedBgId()).map((lookDefinition) =>
      this.looksStore.getLookByLookDefinition(lookDefinition)
    );
  }

  getSelectedLookCategory() {
    return this.looksStore.getCategoryByLayoutThemeId(this.getSelectedLook().layoutThemeID);
  }

  getBgLooksByCategory(category) {
    const selectedOrderLineDetailIndex = this.getSelectedOrderLineDetailIndex(true);

    return this.getBgByCategory(category).map((background) => ({
      backgroundImageId: background.backgroundImageId,
      ...background.look,
      label: this.getPremiumUpgradeLabel(selectedOrderLineDetailIndex, background)
    }));
  }

  getBgByCategory(category) {
    const categoryLookBgs = this.looksStore.getCategoryLooks(category).map((look) => look.background);

    return uniqueBy(categoryLookBgs, (bg) => bg.backgroundImageId).filter((background) =>
      this._isBgAvailable(background)
    );
  }

  getPremiumUpgradeLabel(orderLineDetailIndex, background) {
    if (!background.premiumUpgrade) {
      return null;
    }

    if (this.isPremiumBgIncludedForItem(orderLineDetailIndex, background)) {
      return '+$0';
    }

    return `+\$${background.premiumUpgrade.unitPrice}`;
  }

  isPremiumBgIncludedForItem(orderLineDetailIndex, background) {
    if (!background) {
      return false;
    }

    return (
      this._wasBgSelectedInOtherItems(orderLineDetailIndex, background) ||
      this._isBgInCart(background.backgroundImageId) ||
      this._isPremiumUpgradeInProductUpgrades(background.premiumUpgrade)
    );
  }

  _setOrderLines(orderLines) {
    this.orderLines = orderLines;
    this._refreshIndexMap();
  }

  _refreshIndexMap() {
    this._indexMapMasked = [];
    this._indexMap = [];

    this.orderLines.forEach((orderLine, orderLineIndex) => {
      orderLine.orderLineDetails.forEach((orderLineDetail, orderLineDetailIndex) => {
        const index = [orderLineIndex, orderLineDetailIndex];

        this._indexMapMasked.push(index);

        if (!orderLineDetail.masked) {
          this._indexMap.push(index);
        }
      });
    });
  }

  _initBgSelections() {
    this._forEachOrderLineDetail((orderLineDetail, index) => {
      if (this._isOrderLineDetailCustomizable(orderLineDetail)) {
        this._updateLook(index, orderLineDetail.look, true);
      }
    }, true);
  }

  _withBgSelection(look, selection) {
    return {
      ...look,
      background: {
        ...look.background,
        selection
      }
    };
  }

  _setProductImages() {
    this._forEachOrderLineDetail((orderLineDetail, index) => {
      if (this.itemService.isProductImage(orderLineDetail, this.getOrderLine(index))) {
        this._setOrderLineDetail(index, {
          ...orderLineDetail,
          previewImageNon: this._getProductImage(orderLineDetail)
        });
      }
    });
  }

  _getProductImage(orderLineDetail) {
    return this.contentStore.getProductImage(orderLineDetail.item.skuCode, orderLineDetail.item.defaultLayoutThemeId);
  }

  _selectDefaultLooks() {
    if (this.context.editableOrderLine || !this.jobStore.isInPilot(Pilot.MULTI_IMAGE)) {
      return;
    }

    let prevProductSkuCode = null;
    let prevItemSkuCode = null;
    let sheet = -1;
    let packageDefaultLooks = null;

    this._forEachOrderLineDetail((orderLineDetail, index) => {
      if (!this._isOrderLineDetailCustomizable(orderLineDetail)) {
        return;
      }

      const productSkuCode = this.getOrderLine(index).orderable?.skuCode;

      if (!productSkuCode) {
        return;
      }

      if (productSkuCode !== prevProductSkuCode) {
        packageDefaultLooks = this._getPackageDefaultLooks(productSkuCode);
      }

      if (!packageDefaultLooks) {
        return;
      }

      const itemSkuCode = orderLineDetail.item.skuCode;
      const itemDefaultLooks = packageDefaultLooks[itemSkuCode];

      if (!itemDefaultLooks) {
        console.warn(
          `[Package Item Default Look] Missing default looks for package=${productSkuCode}, item=${itemSkuCode}`
        );
        return;
      }

      if (prevItemSkuCode !== itemSkuCode) {
        sheet = 1;
      } else {
        sheet += 1;
      }

      const look = itemDefaultLooks[sheet];

      if (!look) {
        console.warn(
          `[Package Item Default Look] Missing default look for package=${productSkuCode}, item=${itemSkuCode}, sheet=${sheet}`
        );
        return;
      }

      this._updateLook(index, look);

      prevProductSkuCode = productSkuCode;
      prevItemSkuCode = itemSkuCode;
    });
  }

  _isDigitalPackage() {
    let isDigital = true;
    this._forEachOrderLineDetail((orderLineDetail) => {
      if (orderLineDetail.item.productCategory && orderLineDetail.item.productCategory !== DIGITAL_IMAGE_CATEGORY) {
        isDigital = false;
      }
    });
    return isDigital;
  }

  _getCandidateBackgroundsAcrossCategories() {
    const seenBgIds = new Set();
    const candidates = [];
    this.looksStore.getLookCategories().forEach((category) => {
      this.getBgByCategory(category).forEach((bg) => {
        if (seenBgIds.has(bg.backgroundImageId) || bg.premiumUpgrade) return;
        seenBgIds.add(bg.backgroundImageId);
        candidates.push(bg);
      });
    });
    return candidates;
  }

  _buildDefaultLooksForDigital(packageSkuCode) {
    const defaultLook = this.looksStore.getDefaultLook();
    const defaultLookDefinition = this.looksStore.getLookDefinitionByLook(defaultLook);
    const candidates = this._getCandidateBackgroundsAcrossCategories();

    const entries = [];
    const sheetByItem = {};
    let bgIndex = 0;

    this._forEachOrderLineDetail((orderLineDetail, index) => {
      if (!this._isOrderLineDetailCustomizable(orderLineDetail)) return;
      if (this.getOrderLine(index).orderable?.skuCode !== packageSkuCode) return;

      const itemSkuCode = orderLineDetail.item.skuCode;
      sheetByItem[itemSkuCode] = (sheetByItem[itemSkuCode] || 0) + 1;
      const sheet = sheetByItem[itemSkuCode];

      const look = this._findLookByCropClickAndBgId(
        defaultLookDefinition.crop,
        defaultLookDefinition.preferredClick,
        candidates[bgIndex++].backgroundImageId
      );

      entries.push({ itemSkuCode, packageSkuCode, sheet, layoutThemeId: look.layoutThemeID });
    });

    return entries;
  }

  // TODO: move to `LooksStore` and optimize
  _getPackageDefaultLooks(packageSkuCode) {
    const packageItemDefaultLooks = this._resolvePackageItemDefaultLooks(packageSkuCode);

    if (!packageItemDefaultLooks) {
      return null;
    }

    return packageItemDefaultLooks
      .filter((defaultLook) => defaultLook.packageSkuCode === packageSkuCode)
      .reduce((acc, defaultLook) => {
        const itemSkuCode = defaultLook.itemSkuCode;
        const sheet = defaultLook.sheet;

        const itemDefaultLooks = (acc[itemSkuCode] ??= {});

        if (itemDefaultLooks[sheet]) {
          console.warn(
            `[Package Item Default Look] Duplicate default look for package=${packageSkuCode}, item=${itemSkuCode}, sheet=${sheet}`
          );
        }

        const look = this.looksStore.getLookByLayoutThemeId(defaultLook.layoutThemeId);

        if (!look) {
          console.warn(
            `[Package Item Default Look] Default look not found for package=${packageSkuCode}, item=${itemSkuCode}, sheet=${sheet}, layoutThemeId=${defaultLook.layoutThemeId}`
          );
        }

        itemDefaultLooks[sheet] = look;

        return acc;
      }, {});
  }

  _resolvePackageItemDefaultLooks(packageSkuCode) {
    const seasonEntries = this.jobStore.getJob().season?.packageItemDefaultLooks;
    const hasSeasonEntriesForPackage = seasonEntries?.some((entry) => entry.packageSkuCode === packageSkuCode);

    if (hasSeasonEntriesForPackage) {
      return seasonEntries;
    }

    return this._isDigitalPackage() ? this._buildDefaultLooksForDigital(packageSkuCode) : null;
  }

  _updateLook(index, look, masked = false) {
    const bgSelectionByItem = this._getBgSelectionByItem(look.background);
    const bgSelection = Math.max(...bgSelectionByItem) + 1;

    look = this._withBgSelection(look, bgSelection);

    this._setOrderLineDetail(index, new OrderLineDetail(this.getOrderLineDetail(index, masked).item, look), masked);
  }

  _setOrderLineDetail(index, orderLineDetail, masked = false) {
    const [orderLineIndex, orderLineDetailIndex] = this._getIndexMap(masked)[index];

    this.orderLines[orderLineIndex].orderLineDetails[orderLineDetailIndex] = orderLineDetail;
  }

  _getIndexMap(masked = false) {
    return masked ? this._indexMapMasked : this._indexMap;
  }

  _getSelectedBackground() {
    return this.getSelectedLook().background;
  }

  _isOrderLineDetailCustomizable(orderLineDetail) {
    return orderLineDetail.item?.lookSelectionAllowed;
  }

  _findLookByCropClickAndBgId(crop, click, bgId) {
    const lookDefinitions = this._getLookDefinitionsByBgId(bgId);

    let lookDefinition = lookDefinitions.find((el) => el.crop === crop && el.preferredClick === click);

    if (!lookDefinition) {
      lookDefinition = lookDefinitions[0];
    }

    return this.looksStore.getLookByLookDefinition(lookDefinition);
  }

  _getLookDefinitionsByBgId(bgId) {
    return this.looksStore
      .getLookDefinitionsByBgId(bgId)
      .filter((lookDefinition) => this._isLookAllowed(lookDefinition));
  }

  _updateSelectedLook(look) {
    this._updateLook(this._selectedOrderLineDetailIndex, look);
  }

  _forEachOrderLineDetail(callback, masked = false) {
    for (let i = 0; i < this._getIndexMap(masked).length; i++) {
      callback(this.getOrderLineDetail(i, masked), i);
    }
  }

  _wasBgSelectedInOtherItems(orderLineDetailIndex, background) {
    const bgSelectionByItem = this._getBgSelectionByItem(background);
    const itemSelection = bgSelectionByItem[orderLineDetailIndex];

    const bgSelections = bgSelectionByItem.filter((selection) => selection !== -1);

    return bgSelections.length && itemSelection !== Math.min(...bgSelections);
  }

  _getBgSelectionByItem(background) {
    let selections = [];

    this._forEachOrderLineDetail((orderLineDetail) => {
      let resolvedSelection = -1;
      if (this._isBackgroundMatch(background, orderLineDetail)) {
        const selection = orderLineDetail.look.background.selection;

        if (selection != null) {
          resolvedSelection = selection;
        }
      }

      selections.push(resolvedSelection);
    }, true);

    return selections;
  }

  _isBgAvailable(background) {
    const isLockedPremiumBg = Boolean(background.premiumUpgrade) && this._isPremiumBgLocked(background);

    return !isLockedPremiumBg && this._isBgAllowed(background);
  }

  _isPremiumBgLocked(background) {
    const unlockedBy = background.premiumUpgrade.unlockedBy;

    return (
      unlockedBy &&
      !unlockedBy.has(this.context.productSkuCode) &&
      !this.context.subject.unlockedPremiumBgs.has(background.backgroundImageId)
    );
  }

  _isBgInCart(bgId) {
    return this.context.cartBgIds.has(bgId);
  }

  _isBgAllowed(background) {
    const lookDefinitions = this._getLookDefinitionsByBgId(background.backgroundImageId);

    return lookDefinitions.length > 0;
  }

  _isLookAllowed(lookDefinition) {
    const { layoutThemeId, backgroundImageId: bgId } = lookDefinition;
    const { defaultLookBg, lookWhitelist, lookBlacklist } = this.context;

    const isDefaultLookBg = bgId === defaultLookBg?.backgroundImageId;
    const isWhitelistedLook = !Boolean(lookWhitelist) || lookWhitelist.has(layoutThemeId);
    const isBlacklistedLook = Boolean(lookBlacklist) && lookBlacklist.has(layoutThemeId);

    return isDefaultLookBg || (isWhitelistedLook && !isBlacklistedLook);
  }

  _getCartBgIds(editableOrderLine) {
    return new Set(
      this.shoppingCartStore
        .getCurrentSubject()
        .getOrderLines()
        .filter((orderLine) => orderLine !== editableOrderLine)
        .flatMap((orderLine) => this._toBgIds(orderLine.orderLineDetails))
    );
  }

  _toBgIds(orderLineDetails) {
    return orderLineDetails
      ?.map((orderLineDetail) => orderLineDetail.look?.background?.backgroundImageId)
      .filter((bgId) => Boolean(bgId));
  }

  _isBackgroundMatch(background, orderLineDetail) {
    return (
      this._isOrderLineDetailCustomizable(orderLineDetail) &&
      orderLineDetail.look?.background?.backgroundImageId === background.backgroundImageId
    );
  }

  _isPremiumUpgradeInProductUpgrades(premiumUpgrade) {
    const currentSubject = this.context.subject;

    return [...this.orderLines, ...currentSubject.packages, ...currentSubject.bundles]
      .flatMap((orderLine) => orderLine.orderable?.upgrades)
      .filter((upgrade) => upgrade != null)
      .some((upgrade) => upgrade.skuCode === premiumUpgrade.skuCode);
  }
}

CustomizationImageService.$inject = [
  'LooksStore',
  'ShoppingCartStore',
  'ContentStore',
  'ItemService',
  'JobStore',
  'LookAllowlistService'
];
App.service('CustomizationImageService', CustomizationImageService);
